import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuditLogQuery, fetchAllAuditLogRows, AUDIT_LOG_EXPORT_LIMIT } from '@/hooks/useAuditLogQuery';
import clsx from 'clsx';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Button } from '@/components/ui/button';
import { DateRange } from 'react-day-picker';
import { useUserStore } from '@/store/userStore';

import i18n from '@/i18n';

const getCsvColumns = (): { label: string; key: string }[] => [
  { label: i18n.t('auditLog.columns.date', { ns: 'appui' }), key: "created_at" },
  { label: i18n.t('auditLog.columns.fullName', { ns: 'appui' }), key: "full_name" },
  { label: i18n.t('auditLog.columns.action', { ns: 'appui' }), key: "action" },
  { label: i18n.t('auditLog.columns.target', { ns: 'appui' }), key: "target_type" },
  { label: i18n.t('auditLog.columns.bookingDate', { ns: 'appui' }), key: "booking_date" },
  { label: i18n.t('auditLog.columns.classTime', { ns: 'appui' }), key: "class_time" },
  { label: i18n.t('auditLog.columns.className', { ns: 'appui' }), key: "class_name" },
];

// date-fns v3 lève sur une date invalide, ce qui vide l'écran en plein rendu.
const safeFormat = (value: unknown, pattern: string) => {
  if (!value) return '–';
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? '–' : format(d, pattern);
};

const toCsvValue = (value: unknown) => {
  const str = value === null || value === undefined ? '' : String(value);
  return `"${str.replace(/"/g, '""')}"`;
};

export default function AuditLogPage() {
  const { t } = useTranslation('appui');
  const gymId = useUserStore((state) => state.gymId);
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [fullNames, setFullNames] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<any>(null);

  const pageSize = 20;
  
  // Sync dateRange changes to startDate/endDate for query
  // `toISOString()` convertit en UTC et décalait le jour en fuseau négatif :
  // on garde la date telle que l'utilisateur l'a choisie (locale).
  useEffect(() => {
    const sd = dateRange?.from && !isNaN(dateRange.from.getTime())
      ? format(dateRange.from, 'yyyy-MM-dd')
      : '';
    const ed = dateRange?.to && !isNaN(dateRange.to.getTime())
      ? format(dateRange.to, 'yyyy-MM-dd')
      : '';
    setStartDate(sd);
    setEndDate(ed);
    setPage(0);
  }, [dateRange]);

  const { data, isLoading, refetch } = useAuditLogQuery({ search, userIds, startDate, endDate, fullNames }, page, pageSize);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasNextPage = (page + 1) * pageSize < total;
  const totalPages = Math.ceil(total / pageSize);
  
  const nextPage = () => setPage((p) => p + 1);
  const prevPage = () => setPage((p) => Math.max(0, p - 1));

  const handleExportCsv = async () => {
    if (!gymId) return;
    setIsExporting(true);
    try {
      const { rows: allRows, truncated } = await fetchAllAuditLogRows(gymId, {
        search, userIds, startDate, endDate, fullNames,
      });

      const csvColumns = getCsvColumns();
      const header = csvColumns.map(c => toCsvValue(c.label)).join(',');
      const body = allRows
        .map(row => csvColumns.map(c => toCsvValue(row[c.key])).join(','))
        .join('\n');
      // BOM pour qu'Excel lise correctement les accents.
      const blob = new Blob(['\uFEFF' + header + '\n' + body], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit-log.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (truncated) {
        toast.warning(
          t('auditLog.exportLimited', { limit: AUDIT_LOG_EXPORT_LIMIT })
        );
      } else {
        toast.success(t('auditLog.exportSuccess', { count: allRows.length }));
      }
    } catch (error) {
      console.error('[AuditLogPage] export CSV:', error);
      toast.error(t('auditLog.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleRefresh = () => {
    setSearch('');
    setUserIds([]);
    setFullNames([]);
    setDateRange({ from: null, to: null });
    setPage(0);
    refetch();
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <h1 className="text-xl font-bold">🕵️ Audit Log</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mt-4">
        <Input
          placeholder={t('auditLog.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[160px] md:w-52"
        />

        <Input
          type="text"
          placeholder={t('auditLog.clientNamePlaceholder')}
          value={fullNames.join(',')}
          onChange={(e) => setFullNames(e.target.value.split(',').map(s => s))}
          className="flex-1 min-w-[160px] md:w-60"
        />

        <DateRangePicker
          initialDateRange={dateRange}
          onUpdate={setDateRange}
          className="flex-1 min-w-[200px] md:w-[260px]"
        />

        <Button
          variant="outline"
          onClick={handleRefresh}
          className="flex-shrink-0"
        >
          {t('auditLog.refresh')}
        </Button>

        {/* L'export ne portait que sur la page affichée : il relit maintenant
            toutes les lignes correspondant aux filtres. */}
        <Button
          variant="outline"
          onClick={handleExportCsv}
          disabled={isExporting || !gymId}
          className="flex-shrink-0"
        >
          {isExporting ? t('auditLog.exporting') : t('auditLog.exportCsv')}
        </Button>

      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded shadow">
        {isLoading ? (
          <div className="p-4 text-center">{t('auditLog.loading')}</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">{t('auditLog.columns.date')}</th>
                <th className="p-2 text-left">{t('auditLog.user')}</th>
                <th className="p-2 text-left">{t('auditLog.columns.action')}</th>
                <th className="p-2 text-left">{t('auditLog.target')}</th>
                <th className="p-2 text-left">{t('auditLog.bookingDate')}</th>
                <th className="p-2 text-left">{t('auditLog.classTime')}</th>
                <th className="p-2 text-left">{t('auditLog.details')}</th>
              </tr>
            </thead>
            <tbody>
              {rows?.length ? rows.map((item) => (
                <tr
                  key={item.id}
                  className={clsx(
                    'border-t hover:bg-gray-50 cursor-pointer',
                    item.action?.toLowerCase().includes('delete') && 'bg-red-100'
                  )}
                  onClick={() => setSelected(item)}
                >
                  <td className="p-2">{safeFormat(item.created_at, 'yyyy-MM-dd HH:mm')}</td>
                  <td className="p-2">
                    {item.full_name || '–'}
                    <div className="text-xs text-gray-500">{item.user_id}</div>
                  </td>
                  <td className="p-2">{item.action}</td>
                  <td className="p-2">{item.target_type || '–'}</td>
                  <td className="p-2">
                    {safeFormat(item.booking_date, 'yyyy-MM-dd')}
                  </td>
                  <td className="p-2">
                    {item.class_time || '–'}
                  </td>
                  <td className="p-2 truncate">
                    {item.class_name || '–'}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="text-center p-4">{t('auditLog.noData')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <Button
          variant="outline"
          disabled={page === 0}
          onClick={prevPage}
          className="disabled:opacity-50"
        >
          {t('auditLog.previous')}
        </Button>
        <span className="text-sm">
          {t('auditLog.pageOf', { page: page + 1, totalPages })}
        </span>
        <Button
          variant="outline"
          onClick={nextPage}
          disabled={!hasNextPage}
          className="disabled:opacity-50"
        >
          {t('auditLog.next')}
        </Button>
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded shadow max-w-lg w-full p-4 space-y-2 relative">
            <button
              className="absolute top-2 right-2 text-gray-500"
              onClick={() => setSelected(null)}
            >
              ✖️
            </button>
            <h2 className="text-lg font-bold">{t('auditLog.detailsTitle')}</h2>
            <pre className="bg-gray-50 p-2 rounded overflow-x-auto text-xs">
              {JSON.stringify(selected, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
